import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { Col, Form, OverlayTrigger, Row } from 'react-bootstrap';
import {
  FormCheckbox,
  TextButton,
  DropdownMenu,
  PopoverTooltipV2 as PopoverTooltip,
} from '@praktio/common-ui';
import React, { useState, useRef, useEffect } from 'react';
import { cloneDeep, debounce, isEqual, sortBy } from 'lodash';

import { RootState } from '../../../ducks/store';
import { SortOptions } from '../../../utils/enums';
import { isPlatformAdmin } from '../../../utils/functions';
import { highlightMatch } from '../../../utils';
import {
  TreeNodeData,
  NodeByIdMap,
  ExpandedNodesMap,
  SelectedNodesMap,
  ITreeNodeProps,
  ITreeProps,
} from './Tree.types';

import './Tree.scss';

const TriState = {
  All: 'ALL',
  None: 'NONE',
  Some: 'SOME',
};

const setAllNodes = (nodeById: NodeByIdMap, expanded?: boolean): ExpandedNodesMap => {
  const updated: ExpandedNodesMap = {};
  Object.keys(nodeById).forEach((k) => {
    if (nodeById[k].children) {
      updated[k] = !!expanded;
    }
  });

  return updated;
};

/** Collects all non-disabled descendants of the given node via BFS. */
const getSelectableDescendants = (node: TreeNodeData): TreeNodeData[] => {
  const result: TreeNodeData[] = [];
  const queue = [...(node.children || [])];
  let item = queue.shift();

  while (item) {
    if (item.children) {
      queue.push(...item.children);
    }

    if (!item.disabled) {
      result.push(item);
    }

    item = queue.shift();
  }

  return result;
};

/**
 * Checks whether every non-disabled descendant of the given node is selected.
 * Note: O(n) BFS per node — acceptable for typical team trees but may need
 * memoisation if tree sizes grow significantly.
 */
const areAllSelectableDescendantsSelected = (
  node: TreeNodeData,
  selectedNodes: SelectedNodesMap,
): boolean => {
  const descendants = getSelectableDescendants(node);
  if (descendants.length === 0) return false;
  const allDescendantsSelected = descendants.every((d) => selectedNodes[d._id] === TriState.All);
  const selfSelected = node.disabled || selectedNodes[node._id] === TriState.All;
  return allDescendantsSelected && selfSelected;
};

const TreeNode = ({
  node,
  expandedNodes,
  selectedNodes,
  onExpandClick,
  onSelectClick,
  onSelectDescendants,
  renderName,
  depth,
  treeId,
  searchValue,
}: ITreeNodeProps) => {
  const checkboxRef = useRef(null);

  const { name, _id, disabled } = node;
  const expanded = expandedNodes[_id];
  const state = selectedNodes[_id] || TriState.None;

  const hasChildren = !!node.children?.length;
  const childClassName = `is-child-${depth}`;
  const showExpandIcon = hasChildren;
  const showCheckbox = !!onSelectClick;
  const showNameOnly = !onSelectClick;

  // Note: nameDisplay can be JSX.Element when using renderName or highlightMatch.
  // FormCheckbox types are restrictive (string only) but it renders ReactNode at runtime.
  const nameDisplay: React.ReactNode = renderName
    ? renderName(node, searchValue)
    : searchValue
    ? highlightMatch(name, searchValue)
    : name;

  const hasSelectableDescendants = hasChildren && !!onSelectDescendants;
  const allDescendantsSelected =
    hasSelectableDescendants && areAllSelectableDescendantsSelected(node, selectedNodes);
  const showDisabledCheckbox = showCheckbox && disabled;
  const showEnabledCheckbox = showCheckbox && !disabled;

  const selectDescendantsButton = hasSelectableDescendants ? (
    <OverlayTrigger
      placement="top"
      overlay={
        <PopoverTooltip
          id={`tooltip-select-descendants-${_id}`}
          paragraph={allDescendantsSelected ? 'Deselect all sub-teams' : 'Select all sub-teams'}
        />
      }
    >
      <button
        type="button"
        className="TreeNode-select-descendants"
        onClick={(e) => {
          e.stopPropagation();
          if (onSelectDescendants) onSelectDescendants(node);
        }}
      >
        <span className="material-icons">
          {allDescendantsSelected ? 'remove_done' : 'done_all'}
        </span>
      </button>
    </OverlayTrigger>
  ) : null;

  useEffect(() => {
    if (checkboxRef?.current) {
      (checkboxRef.current as any).indeterminate = state === TriState.Some;
    }
  }, [state]);

  return (
    <div
      className={classNames('TreeNode', {
        'has-children': hasChildren,
        expanded,
        disabled,
        [childClassName]: !!depth,
      })}
    >
      <div className={classNames('TreeNode-spacing')}>
        {showExpandIcon ? (
          <span
            onClick={() => onExpandClick(node)}
            className={classNames('material-icons TreeNode-icon')}
          >
            {expanded ? 'expand_more' : 'chevron_right'}
          </span>
        ) : null}
      </div>

      {showDisabledCheckbox ? (
        <div className="TreeNode-row">
          <OverlayTrigger
            placement="right"
            overlay={
              <PopoverTooltip
                id={`tooltip-disabled-${_id}`}
                paragraph="No reporting data available for this team"
              />
            }
          >
            <Form.Group controlId={_id}>
              <FormCheckbox
                id={`${treeId}${_id}`}
                label={nameDisplay as string}
                onChange={() => null}
                checked={state === TriState.All}
                disabled
              />
            </Form.Group>
          </OverlayTrigger>

          {selectDescendantsButton}
        </div>
      ) : null}

      {showEnabledCheckbox ? (
        <div className="TreeNode-row">
          <Form.Group controlId={_id}>
            <FormCheckbox
              id={`${treeId}${_id}`}
              label={nameDisplay as string}
              onChange={() => onSelectClick(node)}
              checked={state === TriState.All}
              disabled={false}
            />
          </Form.Group>

          {selectDescendantsButton}
        </div>
      ) : null}

      {showNameOnly ? <div className="TreeNode-name">{nameDisplay}</div> : null}
    </div>
  );
};

/**
 * Sorts the data based on the sortOrder
 * @param data - The tree data to sort
 * @param sortOrder - The sort order to use
 * @returns The sorted data
 */
const sortData = (data: any[], sortOrder: SortOptions) => {
  const nameField = 'name';
  const createdAtField = 'createdAt';

  const nameSorter = (parent: any) => parent.name.toLowerCase();

  if (sortOrder === SortOptions.NAME_ASC) {
    return sortBy(data, [nameSorter], nameField);
  } else if (sortOrder === SortOptions.NAME_DESC) {
    return sortBy(data, [nameSorter], nameField).reverse();
  } else if (sortOrder === SortOptions.CREATED_AT_ASC) {
    return sortBy(data, createdAtField);
  } else if (sortOrder === SortOptions.CREATED_AT_DESC) {
    return sortBy(data, createdAtField).reverse();
  }
};

const Tree = ({
  data,
  binarySelect,
  noCascadeDown,
  hideExpandCollapse,
  isLocked = false,
  initialSelections,
  startExpanded,
  searchable,
  hasClearAll,
  id,
  error,
  renderName,
  onSelectedChange,
}: ITreeProps) => {
  let initialSelectedNodes: IKey = {};
  if (initialSelections && data) {
    initialSelections.forEach((id) => {
      initialSelectedNodes[id] = TriState.All;
    });
  }

  const originalDataSet = useRef(false); // Used to determine if the original data has been stored

  const { isAdmin } = useSelector((state: RootState) => ({
    isAdmin: isPlatformAdmin(state.auth.authenticatedUser),
  }));

  const prevData = useRef<any[] | undefined>();
  const [nodeById, setNodeById] = useState<IKey>({});
  const [expandedNodes, setExpandedNodes] = useState<IKey>({});
  const [selectedNodes, setSelectedNodes] = useState<IKey>(initialSelectedNodes);
  const [inputValue, setInputValue] = useState(''); // Immediate input state for responsive typing
  const [searchValue, setSearchValue] = useState(''); // Debounced search value for actual filtering

  const [treeData, setTreeData] = useState<any[]>([]);
  const [orderedNodes, setOrderedNodes] = useState<any[]>([]);
  const [allExpanded, setAllExpanded] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [hasMultipleTopLevelNodes, setHasMultipleTopLevelNodes] = useState(true);
  const [sortOrder, setSortOrder] = useState<SortOptions>(
    isAdmin ? SortOptions.NAME_ASC : SortOptions.CREATED_AT_DESC,
  );

  const showSearchBar = !!searchable;
  const showError = !!error;
  const showSortTooltip = !hasMultipleTopLevelNodes;
  const allowSelection = !!onSelectedChange;
  const allowDescendantSelection = allowSelection && (binarySelect || noCascadeDown);

  // If data or startExpanded changes, reprocess
  useEffect(() => {
    if (!isEqual(data, prevData.current)) {
      const byId: IKey = {};
      const queue = cloneDeep(data);
      let item = queue.shift();
      while (item) {
        const { _id, children } = item;
        byId[_id] = item;
        if (children) {
          queue.unshift(...children);
        }

        item = queue.shift();
      }

      setNodeById(byId);
      if (startExpanded) {
        setExpandedNodes(setAllNodes(byId, true));
        setAllExpanded(true);
      }

      const selections: IKey = {};
      if (initialSelections && data) {
        initialSelections.forEach((id) => {
          selections[id] = TriState.All;
        });
      }

      setSelectedNodes(selections);
    }

    prevData.current = cloneDeep(data);
  }, [data, initialSelections, startExpanded, allExpanded]);

  // Debounced handler that updates the search value used for filtering
  // Input value updates immediately for responsive typing, search value updates after delay
  const debouncedSetSearchValue = useRef(
    debounce((value: string) => {
      setSearchValue(value);
    }, 250),
  ).current;

  const onSearchChange = (value: string) => {
    setInputValue(value); // Immediate update for responsive input
    debouncedSetSearchValue(value); // Debounced update for search filtering
  };

  const onExpandClick = (node: any, expandOverride?: boolean) => {
    const { _id } = node;
    const expanded =
      expandOverride || expandOverride === false ? expandOverride : !expandedNodes[_id];
    setExpandedNodes({ ...expandedNodes, [_id]: expanded });
  };

  const applySelections = (updated: IKey) => {
    setSelectedNodes(updated);

    if (onSelectedChange) {
      const selectedIds = Object.keys(updated).filter(
        (k) => k !== 'All' && updated[k] === TriState.All,
      );
      onSelectedChange(selectedIds);
    }
  };

  const onSelectClick = (node: TreeNodeData) => {
    const updated = cloneDeep(selectedNodes);
    const { _id, parent } = node;
    const state = selectedNodes[_id] !== TriState.All ? TriState.All : TriState.None;

    if (noCascadeDown) {
      updated[_id] = state;
    } else {
      const selectQueue = [node];
      let selectItem = selectQueue.shift();

      while (selectItem) {
        if (selectItem.children) {
          selectQueue.unshift(...selectItem.children);
        }

        updated[selectItem._id] = state;
        selectItem = selectQueue.shift();
      }
    }

    if (!binarySelect && parent) {
      let parentNode = nodeById[parent];
      let childState = state;
      while (parentNode) {
        // Traverse all children to see if anything is selected
        // and if so, mark accordingly
        const queue = [...parentNode.children];
        let item = queue.shift();
        while (item && childState !== TriState.Some) {
          if (item.children) {
            queue.unshift(...item.children);
          }

          const itemState = updated[item._id];
          if (
            (childState === TriState.All && itemState !== TriState.All) ||
            (childState === TriState.None && itemState && itemState !== TriState.None)
          ) {
            childState = TriState.Some;
          }

          item = queue.shift();
        }

        updated[parentNode._id] = childState;
        if (parentNode.parent) {
          parentNode = nodeById[parentNode.parent];
        } else {
          parentNode = undefined;
        }
      }
    }

    applySelections(updated);
  };

  const onSelectDescendants = (node: TreeNodeData) => {
    const updated = cloneDeep(selectedNodes);
    const descendants = getSelectableDescendants(node);
    const includeSelf = !node.disabled;
    const nodesToCheck = includeSelf ? [node, ...descendants] : descendants;
    const allSelected = nodesToCheck.every((d) => updated[d._id] === TriState.All);
    const newState = allSelected ? TriState.None : TriState.All;

    nodesToCheck.forEach((d) => {
      updated[d._id] = newState;
    });

    applySelections(updated);
  };

  const handleClearAll = () => {
    applySelections({});
  };

  const handleSelectAll = () => {
    const updated: IKey = {};

    Object.keys(nodeById).forEach((k) => {
      const node = nodeById[k];
      if (node.disabled) return;
      updated[k] = TriState.All;
    });

    applySelections(updated);
  };

  const handleSortClick = (sortOption: SortOptions) => {
    setSortOrder(sortOption);
    setShowSortMenu(false);
  };

  // Set treeData to data when data changes
  useEffect(() => {
    setTreeData(data);
  }, [data]);

  // Sort treeData when sortOrder changes
  useEffect(() => {
    const sortedData = sortData(treeData, sortOrder) as any[];
    setTreeData(sortedData);

    // We don't want to re-sort the data when the treeData changes
    // eslint-disable-next-line
  }, [sortOrder]);

  useEffect(() => {
    // Skip this effect when searching - the search useEffect handles orderedNodes
    if (searchValue) {
      return;
    }

    // treeData is already sorted by the sortOrder useEffect
    const queue = cloneDeep(treeData);
    let item = queue.shift();
    const depthById: IKey = {};
    const updatedOrderedNodes: any = [];
    const collapsedParents: IKey = {};

    while (item) {
      const { _id, children, parent } = item;

      // Do null coalescing (?? -1) here because if the
      // parent isn't found, it should be top level (0)
      const depth = parent ? (depthById[parent] ?? -1) + 1 : 0;
      depthById[_id] = depth;

      // Render the node if it has no parent/parent doesn't exist, or it's parent is expanded
      if (!parent || !nodeById[parent] || (expandedNodes[parent] && !collapsedParents[parent])) {
        updatedOrderedNodes.push({ ...item, depth });
      } else {
        collapsedParents[_id] = true;
      }

      if (children) {
        queue.unshift(...children);
      }

      item = queue.shift();
    }

    setOrderedNodes(updatedOrderedNodes);

    // Save the original nodes on load
    if (!originalDataSet.current && updatedOrderedNodes.length) {
      setHasMultipleTopLevelNodes(
        updatedOrderedNodes.filter((node: any) => node.depth === 0).length > 1,
      );

      originalDataSet.current = true;
    }
  }, [treeData, nodeById, expandedNodes, searchValue]);

  useEffect(() => {
    if (!searchValue) {
      // When search is cleared, the expand useEffect handles building orderedNodes
      return;
    }

    const searchLower = searchValue.toLowerCase();
    const nodeIds = Object.keys(nodeById);

    // Step 1: Find all nodes that directly match the search
    const matchingNodeIds = new Set<string>();

    for (let i = 0; i < nodeIds.length; i++) {
      const id = nodeIds[i];

      if (nodeById[id].name.toLowerCase().includes(searchLower)) {
        matchingNodeIds.add(id);
      }
    }

    // Step 2: Collect all descendant IDs of matching nodes
    const descendantIds = new Set<string>();

    const collectDescendants = (node: any) => {
      if (node.children) {
        for (let i = 0; i < node.children.length; i++) {
          const child = node.children[i];

          descendantIds.add(child._id);
          collectDescendants(child);
        }
      }
    };

    matchingNodeIds.forEach((id) => {
      const node = nodeById[id];

      if (node) {
        collectDescendants(node);
      }
    });

    // Step 3: Collect all ancestor IDs (memoized to avoid recalculating)
    const ancestorIds = new Set<string>();
    const ancestorCache = new Map<string, string[]>();

    const getAncestors = (nodeId: string): string[] => {
      if (ancestorCache.has(nodeId)) {
        return ancestorCache.get(nodeId)!;
      }

      const node = nodeById[nodeId];

      if (!node?.parent || !nodeById[node.parent]) {
        ancestorCache.set(nodeId, []);
        return [];
      }

      const ancestors = [node.parent, ...getAncestors(node.parent)];

      ancestorCache.set(nodeId, ancestors);

      return ancestors;
    };

    matchingNodeIds.forEach((id) => {
      getAncestors(id).forEach((ancestorId) => ancestorIds.add(ancestorId));
    });

    descendantIds.forEach((id) => {
      getAncestors(id).forEach((ancestorId) => ancestorIds.add(ancestorId));
    });

    // All IDs that should be included in results
    const includedIds = new Set([...matchingNodeIds, ...ancestorIds, ...descendantIds]);

    // Step 4: Calculate depth using memoization
    const depthCache = new Map<string, number>();

    const getDepth = (nodeId: string): number => {
      if (depthCache.has(nodeId)) {
        return depthCache.get(nodeId)!;
      }

      const node = nodeById[nodeId];

      if (!node?.parent || !nodeById[node.parent]) {
        depthCache.set(nodeId, 0);
        return 0;
      }

      const depth = getDepth(node.parent) + 1;

      depthCache.set(nodeId, depth);

      return depth;
    };

    // Step 5: Build ordered list by traversing tree structure
    const updatedOrderedNodes: any[] = [];
    const traverse = (nodes: any[]) => {
      if (!nodes) return;

      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];

        if (includedIds.has(node._id)) {
          updatedOrderedNodes.push({ ...node, depth: getDepth(node._id) });
        }

        if (node.children) {
          traverse(node.children);
        }
      }
    };

    traverse(treeData);

    // Expand all ancestors so matches are visible
    const expanded: IKey = {};

    ancestorIds.forEach((id) => {
      expanded[id] = true;
    });

    setExpandedNodes(expanded);
    setOrderedNodes(updatedOrderedNodes);
    setAllExpanded(true);
  }, [searchValue, nodeById, treeData]);

  // Close the sort menu when clicking outside of it
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const targetEl = e.target as HTMLElement;

      if (!showSortMenu || targetEl.closest('.TextButton') || targetEl.closest('.dropdown-item')) {
        return;
      }

      setShowSortMenu(false);
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [showSortMenu]);

  const actionBar = [];

  if (!hideExpandCollapse) {
    actionBar.push(
      <div
        className="expand-collapse-all"
        onClick={() => {
          setExpandedNodes(setAllNodes(nodeById, !allExpanded));
          setAllExpanded(!allExpanded);
        }}
      >
        <span>{!allExpanded ? 'Expand' : 'Collapse'}</span>
      </div>,
    );
  }

  if (hasClearAll) {
    const noSelectedItems = !Object.values(selectedNodes).some(
      (state) => state === TriState.All || state === TriState.Some,
    );

    actionBar.push(
      <div className="clear-all" onClick={noSelectedItems ? handleSelectAll : handleClearAll}>
        <span>{noSelectedItems ? 'Select All' : 'Clear All'}</span>
      </div>,
    );
  }

  const renderSortButtonText = () => {
    return (
      <strong>
        Sort
        {hasMultipleTopLevelNodes ? (
          <>
            : <span>{sortOrder}</span>
          </>
        ) : null}
      </strong>
    );
  };

  return (
    <>
      <div
        className={classNames('Tree', {
          'is-locked': isLocked,
        })}
      >
        <Row>
          <div className="Tree__SortButton">
            <TextButton
              disabled={!!inputValue || !hasMultipleTopLevelNodes}
              icon="sort"
              onClick={() => setShowSortMenu((prev) => !prev)}
            >
              {showSortTooltip ? (
                <OverlayTrigger
                  placement="right"
                  overlay={
                    <PopoverTooltip
                      id="tooltip-sort"
                      paragraph="Only available for multiple top-level teams"
                    />
                  }
                >
                  {renderSortButtonText()}
                </OverlayTrigger>
              ) : (
                renderSortButtonText()
              )}
            </TextButton>

            <DropdownMenu
              menuOnly={showSortMenu}
              options={[
                {
                  id: '1',
                  label: SortOptions.NAME_ASC,
                  onClick: () => handleSortClick(SortOptions.NAME_ASC),
                },
                {
                  id: '2',
                  label: SortOptions.NAME_DESC,
                  onClick: () => handleSortClick(SortOptions.NAME_DESC),
                },
                {
                  id: '3',
                  label: SortOptions.CREATED_AT_ASC,
                  onClick: () => handleSortClick(SortOptions.CREATED_AT_ASC),
                },
                {
                  id: '4',
                  label: SortOptions.CREATED_AT_DESC,
                  onClick: () => handleSortClick(SortOptions.CREATED_AT_DESC),
                },
              ]}
            />
          </div>

          <div className="Tree__ActionBar">{actionBar}</div>
        </Row>

        <Row>
          <Col key="searchbar" xs={!hideExpandCollapse || hasClearAll ? 4 : 12}>
            {showSearchBar ? (
              <Form.Control
                placeholder="Search..."
                value={inputValue}
                onChange={(event: any) => {
                  onSearchChange(event?.target.value);
                }}
              />
            ) : null}
          </Col>
        </Row>

        <div className="Tree-content">
          {orderedNodes.map((n: any) => (
            <TreeNode
              key={`${id || ''}${n._id}`}
              expandedNodes={expandedNodes}
              selectedNodes={selectedNodes}
              node={n}
              onExpandClick={onExpandClick}
              onSelectClick={allowSelection ? onSelectClick : undefined}
              onSelectDescendants={allowDescendantSelection ? onSelectDescendants : undefined}
              renderName={renderName}
              depth={n.depth}
              treeId={id}
              searchValue={searchValue}
            />
          ))}
        </div>
      </div>

      {showError ? <div className="PraktioFeedback invalid-feedback">{error}</div> : null}
    </>
  );
};

export default Tree;
